/// <amd-module name="Wit/WorkItemForm/Links/Dialogs/AddLinkDialog" />
import { ObservableArray, useDerivedObservable, useObservable } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { Components, useService } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Callout } from "VSSUI/Callout";
import { Dialog } from "VSSUI/Dialog";
import { FormItem } from "VSSUI/FormItem";
import { Icon, IconSize } from "VSSUI/Icon";
import { IListItemDetails, List, ListItem } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { PanelFooter } from "VSSUI/Panel";
import { TextField } from "VSSUI/TextField";
import { Location } from "VSSUI/Utilities/Position";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { LinkTypeFilter, LinkableWorkItemTypeFilter } from "Wit/Common/Utils/WorkItemUtils/LinkTypeFilter";
import { IWorkItemLinkTypeEnd } from "Wit/WorkItemDataManager/Contracts";
import * as Resources from "Wit/WorkItemForm/Resources";
import { LinkTypeForm } from "Wit/WorkItemLinksRegistration/Components";
import { ToolIds } from "Wit/WorkItemLinksRegistration/Constants";
import { ILinkDisplayData, INewLinkData, IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import { WITCustomerIntelligenceArea, WITCustomerIntelligenceFeature } from "Wit/WorkItemModel/OM";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import * as React from "react";
import { useCallback } from "react";
import { ArtifactLink } from "../Controls/ArtifactLink";
import { LinkTypeDropdown } from "../Controls/LinkTypeDropdown";

export interface IAddLinkDialogProps {
    originWorkItems: IWorkItem[];
    onDismiss: () => void;
    onAddLinks: (links: IResolvedLinkDisplayData[]) => void;
    onNewLinksAdded: (newLinkData: { newWorkItemData: INewLinkData; linkTypeName: string; comment: string; assignedTo?: string }) => void;
    linkTypeFilter: LinkTypeFilter;
    linkableWorkItemTypeFilter: LinkableWorkItemTypeFilter;
    isLinkToNewItem: boolean;
}

export interface ILinkTypeFormProps {
    onLinkArtifactsUpdated: (newArtifacts: IResolvedLinkDisplayData[]) => void;
}

// The max number of items to show in the summary callout.
// If we have more than 10 items, we will not show the callout at all
const c_MaximumNumberSummaryItems = 10;

export function AddLinkDialog({
    originWorkItems,
    onDismiss,
    onAddLinks,
    onNewLinksAdded,
    linkTypeFilter,
    linkableWorkItemTypeFilter,
    isLinkToNewItem
}: IAddLinkDialogProps) {
    const [commentObservable, setComment] = useObservable("");
    const [toolObservable, setTool] = useObservable("*");
    const [linkTypeNameObservable, setLinkTypeName] = useObservable("");
    const [witLinkTypeEndObservable, setWitLinkTypeEnd] = useObservable<IWorkItemLinkTypeEnd | undefined>(undefined);
    const [newLinksObservable, setNewLinks] = useObservable<IResolvedLinkDisplayData[]>([]);
    const addLinksDisabledObservable = useDerivedObservable(newLinksObservable, newLinks => newLinks.length === 0, []);
    const [newWorkItemData, setNewWorkItemData] = useObservable<INewLinkData | undefined>(undefined);
    const addNewLinkDisabledObservable = useDerivedObservable(newWorkItemData, newWorkItemData => !newWorkItemData, []);
    const telemetryService = useService<IVssTelemetryService>("IVssTelemetryService");

    const onAddLinksClicked = useCallback(async () => {
        const finalizedArtifacts = newLinksObservable.value.map(linkDisplayData => ({
            ...linkDisplayData,
            linkIdentifier: { ...linkDisplayData.linkIdentifier, Comment: commentObservable.value },
            registeredLinkType: linkTypeNameObservable.value,
            witLinkTypeEnd: witLinkTypeEndObservable.value?.immutableName,
            linkTypeDisplayName: witLinkTypeEndObservable.value?.name ?? linkTypeNameObservable.value
        }));
        onAddLinks(finalizedArtifacts);

        const registeredLinkTypes = finalizedArtifacts.map(artifact => artifact.registeredLinkType).join(",");
        const maxLength = 2000;
        telemetryService.publishEvent(WITCustomerIntelligenceArea.WORK_ITEM_TRACKING, WITCustomerIntelligenceFeature.ADD_LINK, {
            registeredLinkTypes:
                registeredLinkTypes.length > maxLength ? `${registeredLinkTypes.substring(0, maxLength)}...(TRUNCATED)` : registeredLinkTypes
        });
    }, []);

    const onAddNewLinkClicked = useCallback(() => {
        // always add links from existing item to new item which means we should add
        // an opposite link
        let linkType: string;
        if (witLinkTypeEndObservable.value!.isForwardLink) {
            linkType = witLinkTypeEndObservable.value!.linkType?.reverseEnd.name || witLinkTypeEndObservable.value!.oppositeEnd?.name || "";
        } else {
            linkType = witLinkTypeEndObservable.value!.linkType?.forwardEnd.name || "";
        }
        onNewLinksAdded({
            newWorkItemData: newWorkItemData.value!,
            linkTypeName: linkType,
            comment: commentObservable.value,
            assignedTo: originWorkItems[0]?.getFieldValueByName(CoreFieldRefNames.AssignedTo)
        });
    }, []);

    return (
        <Dialog className="add-link-dialog" titleProps={{ text: Resources.AddLink }} onDismiss={onDismiss}>
            <OriginWorkItems originWorkItems={originWorkItems} />

            <div className="link-type-field margin-bottom-8">
                <FormItem label={Resources.LinkTypeLabel}>
                    <LinkTypeDropdown
                        ariaLabel={Resources.LinkTypeLabel}
                        linkTypeFilter={linkTypeFilter}
                        onLinkTypeChanged={lt => {
                            if (
                                lt.registeredLinkType.name === linkTypeNameObservable.value &&
                                lt.registeredLinkType.toolId === toolObservable.value &&
                                lt.workItemLinkTypeEnd?.immutableName === witLinkTypeEndObservable.value?.immutableName
                            ) {
                                return;
                            }
                            setLinkTypeName(lt.registeredLinkType.name);
                            setTool(lt.workItemLinkType?.isRemote ? ToolIds.RemoteWorkItemTracking : lt.registeredLinkType.toolId);
                            setWitLinkTypeEnd(lt.workItemLinkTypeEnd);
                            setNewLinks([]);
                        }}
                    />
                </FormItem>
            </div>

            <Observer tool={toolObservable} linkTypeName={linkTypeNameObservable} linkTypeEnd={witLinkTypeEndObservable}>
                {({ tool, linkTypeName, linkTypeEnd }: { tool: string; linkTypeName: string; linkTypeEnd: IWorkItemLinkTypeEnd | undefined }) => (
                    <div className="link-type-specific-content margin-bottom-8">
                        <LinkTypeForm
                            tool={tool}
                            originWorkItems={originWorkItems}
                            onArtifactsUpdated={setNewLinks}
                            onNewArtifactsUpdated={setNewWorkItemData}
                            linkType={linkTypeName}
                            workItemLinkTypeEnd={linkTypeEnd?.immutableName}
                            isNew={isLinkToNewItem}
                            linkableWorkItemTypeFilter={linkableWorkItemTypeFilter}
                        />
                    </div>
                )}
            </Observer>

            <div className="comment-field">
                <FormItem label={Resources.LinkCommentLabel}>
                    <TextField
                        placeholder={Resources.LinkCommentPlaceholder}
                        value={commentObservable}
                        onChange={(e, val) => {
                            setComment(val);
                        }}
                    />
                </FormItem>
            </div>

            <Observer addLinkDisabled={addLinksDisabledObservable} addNewLinkDisabled={addNewLinkDisabledObservable}>
                {({ addLinkDisabled, addNewLinkDisabled }: { addLinkDisabled: boolean; addNewLinkDisabled: boolean }) => (
                    <PanelFooter
                        buttonProps={[
                            {
                                primary: true,
                                text: Resources.ConfirmAddLink,
                                onClick: isLinkToNewItem ? onAddNewLinkClicked : onAddLinksClicked,
                                disabled: addLinkDisabled && addNewLinkDisabled
                            },
                            { text: Resources.CancelAddLink, onClick: onDismiss }
                        ]}
                    />
                )}
            </Observer>
        </Dialog>
    );
}

function OriginWorkItems({ originWorkItems }: { originWorkItems: IWorkItem[] }) {
    const [calloutOpen, setCalloutOpen] = useObservable(false);

    const originWorkItemDisplayData = new ObservableArray(toLinkDisplayData(originWorkItems));

    const iconElement = React.createRef<HTMLElement>();
    const text: string = format(Resources.LinkOriginMultipleLabel, originWorkItems.length);

    return (
        <div className="origin-work-items">
            <div>{Resources.LinkOriginLabel}</div>

            <div className="padding-vertical-16 padding-left-16">
                {originWorkItems.length === 1 && <ArtifactLink showDetails artifactData={originWorkItemDisplayData.value[0]} />}
                {originWorkItems.length > 1 && (
                    <div className="inline-flex-row">
                        <Icon className={"icon-margin"} iconName={"POI"} size={IconSize.large} />
                        <span className={"margin-right-8"}>{text}</span>
                        {originWorkItems.length < c_MaximumNumberSummaryItems && (
                            <>
                                <span className="scroll-hidden icon-margin link-icon-padding" ref={iconElement}>
                                    <Icon
                                        iconName={"Info"}
                                        size={IconSize.medium}
                                        tooltipProps={{ text: text }}
                                        className={"show-summary-icon"}
                                        ariaLabel={text}
                                        tabIndex={0}
                                        onClick={() => setCalloutOpen(true)}
                                    />
                                </span>

                                <Observer calloutOpen={calloutOpen}>
                                    {(observableProps: { calloutOpen: boolean }) => {
                                        if (observableProps.calloutOpen && iconElement.current) {
                                            return (
                                                <Callout
                                                    anchorElement={iconElement.current}
                                                    anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                                                    contentShadow={true}
                                                    lightDismiss={true}
                                                    onDismiss={() => setCalloutOpen(false)}
                                                >
                                                    <div className="padding-16">
                                                        <List itemProvider={originWorkItemDisplayData} renderRow={renderArtifactLinkRow} />
                                                    </div>
                                                </Callout>
                                            );
                                        }
                                        return null;
                                    }}
                                </Observer>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function renderArtifactLinkRow(rowIndex: number, item: ILinkDisplayData, details: IListItemDetails<ILinkDisplayData>): JSX.Element {
    return (
        <ListItem details={details} index={rowIndex}>
            <ArtifactLink showDetails key={item.displayId} artifactData={item} />
        </ListItem>
    );
}

function toLinkDisplayData(workItems: IWorkItem[]): ILinkDisplayData[] {
    const displayData = workItems.map(workItem => {
        return {
            icons: [
                {
                    project: workItem.getFieldValueByName(CoreFieldRefNames.TeamProject),
                    workItemTypeName: workItem.getFieldValueByName(CoreFieldRefNames.WorkItemType)
                },
                workItem.getFieldValueByName(CoreFieldRefNames.AssignedTo)?.identityRef
            ],
            id: workItem.getFieldValueByName(CoreFieldRefNames.Id),
            displayId: workItem.getFieldValueByName(CoreFieldRefNames.Id)?.toString() ?? "",
            title:
                workItem.getFieldValueByName(CoreFieldRefNames.Title) ||
                format(Resources.PlaceholderNewWorkItemTitle, workItem.getFieldValueByName(CoreFieldRefNames.WorkItemType)),
            state: workItem.getFieldValueByName(CoreFieldRefNames.State),
            updateDate: workItem.getFieldValueByName(CoreFieldRefNames.AuthorizedDate),

            // These are needed in the type but not needed to display the source artifact
            tool: ToolIds.WorkItemTracking,
            linkTypeDisplayName: "",
            registeredLinkType: "",
            uri: "",
            additionalData: { workItem }
        };
    });
    return displayData;
}

Components.add("add-link-dialog", AddLinkDialog);
